import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Check for error in URL first
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        
        if (urlParams.has('error') || hashParams.has('error')) {
          const error = urlParams.get('error') || hashParams.get('error');
          const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');
          
          console.error('Auth callback error from URL:', error, errorDescription);
          
          if (errorDescription?.includes('Database error saving new user')) {
            toast({
              title: "Erro na autenticação",
              description: "Usuário já existe no sistema. Aguarde um momento e tente novamente.",
              variant: "destructive",
            });
          } else {
            toast({
              title: "Erro na autenticação",
              description: "Houve um problema na autenticação com Azure AD. Tente novamente.",
              variant: "destructive",
            });
          }
          
          navigate('/auth');
          return;
        }

        // Check for email confirmation success
        if (urlParams.get('type') === 'signup' || hashParams.get('type') === 'signup') {
          toast({
            title: "Email confirmado!",
            description: "Sua conta foi confirmada com sucesso. Você pode fazer login agora.",
            variant: "default",
          });
          navigate('/auth');
          return;
        }

        // Check for invited user who needs to set password
        if (urlParams.get('type') === 'invite' || hashParams.get('type') === 'invite') {
          toast({
            title: "Convite aceito!",
            description: "Agora defina sua senha para acessar o sistema.",
            variant: "default",
          });
          navigate('/set-password');
          return;
        }
        
        // Supabase will automatically process the OAuth callback from the URL hash
        // since detectSessionInUrl is now enabled. Give it a moment to process.
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Get the session (should be available now)
        const { data: authData, error: authError } = await supabase.auth.getSession();
        
        if (authError) {
          console.error('Auth session error:', authError);
          toast({
            title: "Erro na autenticação",
            description: "Erro ao processar sessão OAuth. Tente novamente.",
            variant: "destructive",
          });
          navigate('/auth');
          return;
        }
        
        const session = authData.session;
        
        if (!session) {
          console.error('No session found after OAuth callback processing');
          toast({
            title: "Erro na autenticação",
            description: "Não foi possível estabelecer a sessão. Tente novamente.",
            variant: "destructive",
          });
          navigate('/auth');
          return;
        }

        // The trigger will handle the user/profile creation automatically
        // Redirect to onboarding for new users or portal for completed users
        toast({
          title: "Login realizado com sucesso",
          description: "Bem-vindo ao sistema!",
        });
        
        // Verificar status do onboarding antes de redirecionar
        const { data: profile } = await supabase
          .from("profiles")
          .select("onboarding_step")
          .eq("id", session.user.id)
          .single();
        
        const onboardingStep = profile?.onboarding_step || "pending";
        
        if (onboardingStep === "completed") {
          navigate('/portal');
        } else {
          navigate('/onboarding/personal');
        }
        
      } catch (error) {
        console.error('Auth callback error:', error);
        toast({
          title: "Erro na autenticação",
          description: "Erro interno na autenticação.",
          variant: "destructive",
        });
        navigate('/auth');
      }
    };

    handleAuthCallback();
  }, [navigate, toast]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Processando autenticação...</p>
      </div>
    </div>
  );
};

export default AuthCallback;